/**
 * Shared API types for the staff web app.
 *
 * Single source of truth is the @enable2/api-types package (apps/shared-types),
 * which mirrors docs/contracts/api-contracts.md and docs/architecture/data-model.md.
 * Re-export here so app code imports from a stable, local module path and the
 * shared package can be swapped for a published artifact in production without
 * touching every call site.
 */
export type {
  ApiError as ApiErrorEnvelope,
  Page,
  Role,
  Case,
  CaseAttempt,
  Patient,
  Employer,
  EmployerContact,
  BenefitPackage,
  Invoice,
  InvoiceLine,
  ConsentRecord,
  AIInteraction,
  Notification,
  PatientStatus,
  PatientDocument,
} from '@enable2/api-types';

export { API_V1 } from '@enable2/api-types';

import type { Role } from '@enable2/api-types';

// ---------------------------------------------------------------------------
// Local wire shapes not (yet) in the shared @enable2/api-types package.
// These mirror the backend Pydantic DTOs (services/<svc>/schemas.py) for the
// staff surfaces that consume them. Kept here so app code imports one module.
// ---------------------------------------------------------------------------

/** A unit of work on a role-scoped queue (services/workflow Task DTO). */
export interface Task {
  id: string;
  tenant_id: string;
  case_id: string;
  queue_id: string;
  /** Owning role queue, e.g. `pas`, `bas`, `state_ops`, `retention`. */
  queue_role: string;
  /** Workflow state the task gates, e.g. `screening`. */
  state: string;
  form_def_id?: string | null;
  /** `open` | `in_progress` | `completed` | `skipped`. */
  status: string;
  /** User the task is assigned to. */
  owner_id?: string | null;
  priority_score: number;
  /** SLA due-by timestamp. */
  due_at?: string | null;
  skip_reason?: string | null;
  created_at: string;
  updated_at: string;
}

/** Cursor-paginated page of tasks (services/workflow TaskPage transport). */
export interface TaskPage {
  items: Task[];
  next_cursor?: string | null;
  has_next?: boolean;
}

/** An append-only case-timeline entry (services/cases CaseEvent DTO). */
export interface CaseEvent {
  id: string;
  case_id: string;
  tenant_id: string;
  /** e.g. `case.created`, `case.attempt_logged`, `case.consent_captured`. */
  event_type: string;
  payload?: Record<string, unknown>;
  actor_id?: string | null;
  created_at: string;
}

/** A human-in-the-loop review item (services/ai_gateway AIReviewTask DTO). */
export interface AIReviewTask {
  id: string;
  tenant_id: string;
  ai_interaction_id: string;
  case_id?: string | null;
  capability: string;
  confidence: number;
  proposed_output?: Record<string, unknown>;
  /** `open` | `resolved`. */
  status: string;
  /** `approve` | `reject` | `amend` once resolved. */
  decision?: string | null;
  resolved_by?: string | null;
  resolution_note?: string | null;
  created_at: string;
  resolved_at?: string | null;
}

/** A reviewer's decision when resolving a HITL review task. */
export type ReviewDecision = 'approve' | 'reject' | 'amend';

/** One record per integration-connector run (services/integration IntegrationJob DTO). */
export interface IntegrationJob {
  id: string;
  tenant_id: string;
  connector: string;
  /** `pull` (inbound) or `push` (outbound). */
  direction: string;
  /** `pending` | `running` | `succeeded` | `retrying` | `dead_lettered` | `deduped`. */
  status: string;
  idempotency_key: string;
  attempts: number;
  error?: string | null;
  payload_ref?: string | null;
  result?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

/** A permanently failed connector operation (services/integration DeadLetter DTO). */
export interface DeadLetter {
  id: string;
  tenant_id: string;
  connector: string;
  direction: string;
  job_id?: string | null;
  idempotency_key: string;
  error: string;
  payload_ref?: string | null;
  attempts: number;
  alerted: boolean;
  first_attempt_at: string;
  last_attempt_at: string;
}

/** The 5 staff roles exposed to role-scoped work queues (FND-005 subset). */
export const STAFF_ROLES: readonly Role[] = [
  'staff_admin',
  'pas',
  'bas',
  'finance',
  'retention',
];
